import { Formiz, useForm } from "@formiz/core";
import { FieldValidationObject } from "@formiz/core/dist/types/field.types";
import { NotificationType } from "@next-platform/client-common-core";
import { Button, ButtonSize, IconVariants, Input, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { TFunction } from "i18next";
import React from "react";

import ConfirmationModal from "@/components/common/ConfirmationModal";
import DiscardChangesModalV2 from "@/components/common/DiscardChangesModalV2";
import ReactRouterPrompt from "@/components/common/ReactRouterPrompt";
import { FormField, Grid } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useToast from "@/hooks/useToast";
import SelectionCard from "@/pages/Lorebook/components/SelectionCard";
import LanguageDropdown from "@/pages/Voice/CloneVoice/components/LanguageDropDown";
import VoiceClipsContainer from "@/pages/Voice/CloneVoice/VoiceClipsContainer";
import VoiceClipsPlaceholdersContainer from "@/pages/Voice/CloneVoice/VoiceClipsPlaceholder";
import TtsGuideLink from "@/pages/Voice/components/TtsGuideLink";
import { VoiceClipPlaceholder, VoiceFormFields, VoiceFormProps } from "@/pages/Voice/types";
import { cn } from "@/utils";

import {
  buildDescriptionValidationRules,
  buildLanguageValidationRules,
  buildNameValidationRules,
  EMPTY_ARRAY,
  getValidationSummary,
  useCloneVoiceFormLogic,
} from "./useCloneVoiceFormLogic";

export interface IValidationRef {
  clips: Record<string, boolean>;
  stopCurrentPlayingClip: null | (() => void);
}

interface IVoiceNameFieldProps {
  stateShowRequiredFormFieldsError: boolean;
  t: TFunction;
}

interface IDescriptionFieldProps {
  stateShowRequiredFormFieldsError: boolean;
  t: TFunction;
}

interface ILanguageFieldProps {
  stateShowRequiredFormFieldsError: boolean;
  language: string;
  stateCloneLanguages: string[];
  stateBackendLanguages: string[];
  stateIsLanguagesLoading: boolean;
  t: TFunction;
}

interface IVoiceClipFieldProps {
  stateShowRequiredFormFieldsError: boolean;
  voiceClipValidationRules: FieldValidationObject[];
  isDragOver: boolean;
  isAtClipLimit: boolean;
  isValidFileType: boolean;
  dragHandlers: React.HTMLAttributes<HTMLElement>;
  handleFilePicker(event?: Event): void;
  t: TFunction;
}

interface IValidationErrorSummaryProps {
  isStepPristine: boolean;
  isValid: boolean;
  stateError: string;
  fields: Record<string, { error?: string }>;
}

interface IClipsAndSubmitProps {
  refValidClips: React.RefObject<IValidationRef>;
  deleteClip(name: string): void;
  deletePlaceholderClip(name: string): void;
  voiceClipValues: File[];
  placeholderClipValues: VoiceClipPlaceholder[];
  onClone?: VoiceFormProps["onClone"];
  stateIsCloneLoading: boolean;
  isSubmitVisuallyDisabled: boolean;
  onSubmitClick(): void;
}

function VoiceNameField({ stateShowRequiredFormFieldsError, t }: IVoiceNameFieldProps) {
  const nameValidationRules = React.useMemo(() => buildNameValidationRules(t), [t]);
  return (
    <FormField
      disabled={false}
      required
      showError={stateShowRequiredFormFieldsError}
      name={VoiceFormFields.VOICE_NAME}
      as="input"
      data-field-name={VoiceFormFields.VOICE_NAME}
      validations={nameValidationRules}
    >
      <Input
        disabled={false}
        label={t("voice:form.create.field.name.label")}
        helperText={t("voice:form.create.field.name.helper")}
        placeholder={t("voice:form.create.field.name.placeholder")}
        maxLength={20}
        aria-label={t("voice:form.create.field.name.label")}
        required
      />
    </FormField>
  );
}

function DescriptionField({ stateShowRequiredFormFieldsError, t }: IDescriptionFieldProps) {
  const descriptionRules = React.useMemo(() => buildDescriptionValidationRules(t), [t]);
  return (
    <FormField
      disabled={false}
      showError={stateShowRequiredFormFieldsError}
      name={VoiceFormFields.DESCRIPTION}
      as="textarea"
      data-field-name={VoiceFormFields.DESCRIPTION}
      validations={descriptionRules}
    >
      <Input
        label={t("voice:form.create.field.description.label")}
        helperText={t("voice:form.create.field.description.helper")}
        placeholder={t("voice:form.create.field.description.placeholder")}
        maxLength={100}
        aria-label={t("voice:form.create.field.description.label")}
        disabled={false}
        required
      />
    </FormField>
  );
}

function LanguageField({
  stateShowRequiredFormFieldsError,
  language,
  stateCloneLanguages,
  stateBackendLanguages,
  stateIsLanguagesLoading,
  t,
}: ILanguageFieldProps) {
  const languageRules = React.useMemo(() => buildLanguageValidationRules(t), [t]);
  return (
    <FormField
      disabled={false}
      showError={stateShowRequiredFormFieldsError}
      required={true}
      name={VoiceFormFields.LANGUAGE}
      as="custom"
      data-field-name={VoiceFormFields.LANGUAGE}
      label={t("voice:form.create.field.language.label")}
      validations={languageRules}
    >
      <LanguageDropdown
        value={language}
        languages={stateCloneLanguages}
        languageCodes={stateBackendLanguages}
        placeholder={t("voice:form.create.field.language.placeholder")}
        helperText={t("voice:form.create.field.language.helper")}
        isLoading={stateIsLanguagesLoading}
      />
    </FormField>
  );
}

const VoiceClipField = ({
  stateShowRequiredFormFieldsError,
  voiceClipValidationRules,
  isDragOver,
  isAtClipLimit,
  isValidFileType,
  dragHandlers,
  handleFilePicker,
  t,
}: IVoiceClipFieldProps) => (
  <FormField
    disabled={false}
    showError={stateShowRequiredFormFieldsError}
    required={true}
    name={VoiceFormFields.VOICE_CLIP}
    as="custom"
    data-field-name={VoiceFormFields.VOICE_CLIP}
    label={t("voice:assignment.cta.attach")}
    validations={voiceClipValidationRules}
  >
    <div
      className={cn("flex flex-col [&_button]:py-7", isDragOver && !isAtClipLimit && (isValidFileType ? "opacity-75" : "opacity-50"))}
      {...(!isAtClipLimit ? dragHandlers : {})}
    >
      <SelectionCard
        label=""
        subLabel={
          <>
            <span className="font-bold">
              <Trans i18nKey="voice:form.create.upload.cta" />
            </span>{" "}
            <Trans i18nKey="voice:form.create.upload.dragAndDrop" />
            <br />
            <span className="text-center block">
              <Trans i18nKey="voice:form.create.upload.fileTypes" />
            </span>
          </>
        }
        icon={IconVariants.UPLOAD}
        onClick={handleFilePicker}
        borderStyle="dotted"
        disabled={isAtClipLimit}
      />
    </div>
  </FormField>
);

const ValidationErrorSummary = ({ isStepPristine, isValid, stateError, fields }: IValidationErrorSummaryProps) => {
  if (isStepPristine || isValid) return null;
  const errors = getValidationSummary(fields);
  return (
    <>
      <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-red-9">
        {stateError}
      </Text>
      {Boolean(errors.length) && (
        <div className="flex flex-col md:flex-row gap-2">
          {errors.map((error: string, index: number) => (
            <div key={index} className="w-full md:w-1/2">
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-red-9">
                {error}
              </Text>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const ClipsAndSubmit = ({
  refValidClips,
  deleteClip,
  deletePlaceholderClip,
  voiceClipValues,
  placeholderClipValues,
  onClone,
  stateIsCloneLoading,
  isSubmitVisuallyDisabled,
  onSubmitClick,
}: IClipsAndSubmitProps) => (
  <>
    <FormField disabled={false} name={VoiceFormFields.PLACEHOLDER_CLIP} as="custom" data-field-name={VoiceFormFields.PLACEHOLDER_CLIP}>
      <VoiceClipsPlaceholdersContainer deleteClip={deletePlaceholderClip} clips={placeholderClipValues} />
    </FormField>
    <VoiceClipsContainer ref={refValidClips} deleteClip={deleteClip} clips={voiceClipValues} />
    <div className="flex flex-col md:flex-row gap-2 justify-end">
      {!!onClone && (
        <Button
          size={ButtonSize.LARGE}
          type="button"
          onClick={onSubmitClick}
          aria-labelledby="clone-voice"
          loading={stateIsCloneLoading}
          loadingWithText={true}
          aria-disabled={isSubmitVisuallyDisabled}
          className={isSubmitVisuallyDisabled ? "opacity-50 cursor-not-allowed" : ""}
        >
          <Trans i18nKey="voice:form.create.cta.submit" />
        </Button>
      )}
    </div>
  </>
);

interface ICloneVoiceFormContentProps {
  props: VoiceFormProps;
  voiceForm: ReturnType<typeof useForm>;
}

const CloneVoiceFormContent = ({ props, voiceForm }: ICloneVoiceFormContentProps) => {
  const logic = useCloneVoiceFormLogic(props, voiceForm);
  const language = props.language ?? "";
  const voiceClipPlaceholders = props.voiceClipPlaceholders ?? EMPTY_ARRAY;
  const { onClone } = props;
  const { t, stateBackendLanguages, stateIsLanguagesLoading, stateCloneLanguages, initialValues } = logic;
  const { stateIsCloneLoading, stateShowRequiredFormFieldsError, stateShowConsentModal, stateError } = logic;
  const { isErrorMode, refValidClips, voiceClipValidationRules, isDragOver, isValidFileType, dragHandlers } = logic;
  const { hasAnyClips, isAtClipLimit, hasFormChanged, hasErroredPlaceholderClip, isValid, isStepPristine, values } = logic;
  const { submitForm, confirmClone, handleConsentModalClose, handleFilePicker } = logic;
  const { deleteClip, deletePlaceholderClip, routerPromptCallback } = logic;
  const { displayToast } = useToast();

  const isSubmitVisuallyDisabled = !(isValid ?? false) || !hasAnyClips || hasErroredPlaceholderClip || (isErrorMode && !hasFormChanged);

  const handleSubmitButtonClick = React.useCallback(() => {
    if (isErrorMode && !hasFormChanged) {
      displayToast({ message: t("error:noChangesDetected"), type: NotificationType.info });
      return;
    }
    void submitForm();
  }, [displayToast, hasFormChanged, isErrorMode, submitForm, t]);

  const handleFormizSubmit = React.useCallback(() => {
    void submitForm();
  }, [submitForm]);
  const handleNativeSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void voiceForm.submit(event);
    },
    [voiceForm],
  );
  return (
    <>
      <ReactRouterPrompt when={routerPromptCallback}>
        {({ isActive, onConfirm, onCancel: onCancelNavigation }: { isActive: boolean; onConfirm: () => void; onCancel: () => void }) =>
          isActive ? (
            <DiscardChangesModalV2
              heading={t("common:modal.unsavedChanges.title")}
              description={t("common:modal.unsavedChanges.message")}
              isVisible={true}
              onUserConfirmation={onConfirm}
              handleClose={onCancelNavigation}
            />
          ) : null
        }
      </ReactRouterPrompt>
      <Grid className="flex justify-between flex-col mob:flex-row">
        <Text variant={TextVariants.HEADING_5_BOLD}>
          <Trans i18nKey="voice:form.create.title" />
        </Text>
        <TtsGuideLink label={t("voice:form.create.guide")} ariaLabel="voice-cloning-guide" navigationKey="custom_voices_docs" />
      </Grid>
      <Formiz connect={voiceForm} initialValues={initialValues} onSubmit={handleFormizSubmit}>
        <form onSubmit={handleNativeSubmit}>
          <fieldset className="flex flex-col gap-6 pt-6 border-none min-w-0">
            <VoiceNameField stateShowRequiredFormFieldsError={stateShowRequiredFormFieldsError} t={t} />
            <DescriptionField stateShowRequiredFormFieldsError={stateShowRequiredFormFieldsError} t={t} />
            <LanguageField
              stateShowRequiredFormFieldsError={stateShowRequiredFormFieldsError}
              language={language}
              stateCloneLanguages={stateCloneLanguages}
              stateBackendLanguages={stateBackendLanguages}
              stateIsLanguagesLoading={stateIsLanguagesLoading}
              t={t}
            />
            <VoiceClipField
              stateShowRequiredFormFieldsError={stateShowRequiredFormFieldsError}
              voiceClipValidationRules={voiceClipValidationRules}
              isDragOver={isDragOver}
              isAtClipLimit={isAtClipLimit}
              isValidFileType={isValidFileType}
              dragHandlers={dragHandlers}
              handleFilePicker={handleFilePicker}
              t={t}
            />
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-left text-gray-11 -mt-3">
              <Trans i18nKey="voice:form.create.upload.helper" />
            </Text>
            <ValidationErrorSummary
              isStepPristine={isStepPristine ?? false}
              isValid={isValid ?? false}
              stateError={stateError}
              fields={voiceForm.fields}
            />
            <ClipsAndSubmit
              refValidClips={refValidClips}
              deleteClip={deleteClip}
              deletePlaceholderClip={deletePlaceholderClip}
              voiceClipValues={values[VoiceFormFields.VOICE_CLIP]}
              placeholderClipValues={values[VoiceFormFields.PLACEHOLDER_CLIP] ?? voiceClipPlaceholders}
              onClone={onClone}
              stateIsCloneLoading={stateIsCloneLoading}
              isSubmitVisuallyDisabled={isSubmitVisuallyDisabled}
              onSubmitClick={handleSubmitButtonClick}
            />
          </fieldset>
        </form>
      </Formiz>
      <ConfirmationModal
        heading={t("voice:modal.consent.title")}
        handleClose={handleConsentModalClose}
        onUserConfirmation={confirmClone}
        confirmationMessage={t("voice:modal.consent.content")}
        isVisible={stateShowConsentModal}
        isLoading={stateIsCloneLoading}
        actionButtonText={t("voice:modal.consent.cta.confirm")}
        discardButtonText={t("common:cancel")}
      />
    </>
  );
};

function CloneVoiceForm(props: VoiceFormProps) {
  const voiceForm = useForm();
  return <CloneVoiceFormContent props={props} voiceForm={voiceForm} />;
}

export default React.memo(CloneVoiceForm);
